import { Matrix, Vector3, Vector4 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';

/**
 * Class containing static functions to help procedurally build meshes
 */
export class SphereBuilder {
    /**
     * Creates a torus mesh
     * * The parameter `diameter` sets the diameter size (float) of the torus (default 1)
     * * The parameter `thickness` sets the diameter size of the tube of the torus (float, default 0.5)
     * * The parameter `tessellation` sets the number of torus sides (postive integer, default 16)
     * * You can also set the mesh side orientation with the values : BABYLON.Mesh.FRONTSIDE (default), BABYLON.Mesh.BACKSIDE or BABYLON.Mesh.DOUBLESIDE
     * * If you create a double-sided mesh, you can choose what parts of the texture image to crop and stick respectively on the front and the back sides with the parameters `frontUVs` and `backUVs` (Vector4). Detail here : https://doc.babylonjs.com/babylon101/discover_basic_elements#side-orientation
     * * The mesh can be set to updatable with the boolean parameter `updatable` (default false) if its internal geometry is supposed to change once created.
     * @param name defines the name of the mesh
     * @param options defines the options used to create the mesh
     * @param scene defines the hosting scene
     * @returns the torus mesh
     * @see https://doc.babylonjs.com/how_to/set_shapes#torus
     */
    public static CreateSphere(
        name: string,
        options: {
            segments?: number;
            diameter?: number;
            diameterX?: number;
            diameterY?: number;
            diameterZ?: number;
            arc?: number;
            slice?: number;
            sliceStart?: number;
            sideOrientation?: number;
            frontUVs?: Vector4;
            backUVs?: Vector4;
            updatable?: boolean;
        },
        scene: any
    ): Mesh {
        const sphere = new Mesh(name, scene);

        options.sideOrientation = Mesh._GetDefaultSideOrientation(options.sideOrientation);
        sphere._originalBuilderSideOrientation = options.sideOrientation;

        const vertexData = createSphere(options);

        vertexData.applyToMesh(sphere, options.updatable);

        return sphere;

        function createSphere(options: {
            segments?: number;
            diameter?: number;
            diameterX?: number;
            diameterY?: number;
            diameterZ?: number;
            arc?: number;
            slice?: number;
            sliceStart?: number;
            sideOrientation?: number;
            frontUVs?: Vector4;
            backUVs?: Vector4;
        }) {
            const segments: number = options.segments || 32;
            const diameterX: number = options.diameterX || options.diameter || 1;
            const diameterY: number = options.diameterY || options.diameter || 1;
            const diameterZ: number = options.diameterZ || options.diameter || 1;
            const arc: number = options.arc && (options.arc <= 0 || options.arc > 1) ? 1.0 : options.arc || 1.0;
            const sliceStart =
                options.sliceStart && (options.sliceStart < 0 || options.sliceStart > 1) ? 0 : options.sliceStart || 0;
            let slice: number = options.slice && options.slice <= 0 ? 1.0 : options.slice || 1.0;
            const sideOrientation =
                options.sideOrientation === 0 ? 0 : options.sideOrientation || VertexData.DEFAULTSIDE;

            if (slice + sliceStart > 1) {
                slice = 1 - sliceStart;
            }

            const radius = new Vector3(diameterX / 2, diameterY / 2, diameterZ / 2);

            const totalZRotationSteps = 2 + segments;
            const totalYRotationSegments = 2 * totalZRotationSteps;
            const totalYRotationSteps = totalYRotationSegments + 1;

            const indices = [];
            const positions = [];
            const normals = [];
            const uvs = [];

            for (let zRotationStep = 0; zRotationStep <= totalZRotationSteps; zRotationStep++) {
                const normalizedZ = zRotationStep / totalZRotationSteps;
                const angleZ = normalizedZ * Math.PI * slice + sliceStart * Math.PI;

                for (let yRotationStep = 0; yRotationStep < totalYRotationSteps; yRotationStep++) {
                    const normalizedY = yRotationStep / totalYRotationSegments;

                    const angleY = normalizedY * Math.PI * 2 * arc;

                    const rotationZ = Matrix.RotationZ(-angleZ);
                    const rotationY = Matrix.RotationY(angleY);
                    const afterRotZ = Vector3.TransformCoordinates(Vector3.Up(), rotationZ);
                    const complete = Vector3.TransformCoordinates(afterRotZ, rotationY);

                    const vertex = complete.multiply(radius);
                    const normal = complete.divide(radius).normalize();

                    positions.push(vertex.x, vertex.y, vertex.z);
                    normals.push(normal.x, normal.y, normal.z);
                    uvs.push(normalizedY, 1 - normalizedZ);

                    if (yRotationStep < totalYRotationSegments || arc === 1) {
                        const nextYStep = (yRotationStep + 1) % totalYRotationSteps;
                        if (sliceStart === 0 && zRotationStep === 0) {
                            indices.push(0); // C
                            indices.push(nextYStep); // B
                            indices.push(yRotationStep); // A
                        } else if (zRotationStep > 0) {
                            indices.push((zRotationStep - 1) * totalYRotationSteps + yRotationStep); // C
                            indices.push(zRotationStep * totalYRotationSteps + nextYStep); // B
                            indices.push(zRotationStep * totalYRotationSteps + yRotationStep); // A

                            indices.push((zRotationStep - 1) * totalYRotationSteps + yRotationStep); // C
                            indices.push((zRotationStep - 1) * totalYRotationSteps + nextYStep); // D
                            indices.push(zRotationStep * totalYRotationSteps + nextYStep); // B
                        }
                    }
                }
            }

            // Sides
            VertexData._ComputeSides(
                sideOrientation,
                positions,
                indices,
                normals,
                uvs,
                options.frontUVs,
                options.backUVs
            );

            // Result
            const vertexData = new VertexData();

            vertexData.indices = indices;
            vertexData.positions = positions;
            vertexData.normals = normals;
            vertexData.uvs = uvs;

            return vertexData;
        }
    }
}
